import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { BrandMark } from "../components/BrandMark";
import { Footer } from "../components/Footer";

const CAPABILITIES: { title: string; body: string }[] = [
  {
    title: "Folders",
    body: "Create nested folders and move images between them. The folder path becomes the URL path, so /products/phones/ maps to a real address.",
  },
  {
    title: "Upload",
    body: "Drop files anywhere on the page or pick them from disk. Several upload at once, each with its own progress, cancel and retry.",
  },
  {
    title: "Names you choose",
    body: "Set the filename while uploading. The extension comes from the verified file type. Add a short unique id or a timestamp when you want no collisions.",
  },
  {
    title: "Direct URLs",
    body: "Every image has a permanent public address, copied with one click. No sign in and no redirect between the link and the file.",
  },
  {
    title: "Finding things",
    body: "Search by filename, folder or type. Filter by format and file size, and sort by date, name or size.",
  },
  {
    title: "Duplicate handling",
    body: "Uploading a name that already exists never overwrites silently. Choose to create a copy, replace the original, or skip the file.",
  },
];

const DELIVERY: { term: string; detail: string }[] = [
  { term: "Image host", detail: "img-cdn.bt4team.com" },
  { term: "Cache policy", detail: "public, max-age=31536000, immutable" },
  { term: "Revalidation", detail: "ETag with 304 responses" },
  { term: "Maximum file size", detail: "25 MiB per image" },
  { term: "Accepted formats", detail: "JPEG, PNG, WebP, GIF, AVIF, BMP, TIFF, ICO" },
  { term: "Infrastructure", detail: "Cloudflare Pages, Pages Functions and D1" },
];

export function LandingPage() {
  const { status } = useAuth();
  const signedIn = status === "authed";
  const target = signedIn ? "/app" : "/login";

  return (
    <div className="flex min-h-full flex-col">
      <header className="glass glass-strong fixed inset-x-0 top-0 z-30 flex h-14 items-center justify-between gap-3 rounded-none border-x-0 border-t-0 px-4 sm:px-6">
        <div className="flex min-w-0 items-center gap-2.5">
          <BrandMark />
          <span className="truncate text-[15px] font-semibold tracking-tight text-zinc-900">Images</span>
          <span className="hidden text-2xs text-zinc-500 sm:inline">by BT4Team</span>
        </div>
        <Link
          to={target}
          className="inline-flex h-9 flex-shrink-0 items-center rounded-lg bg-zinc-900 px-4 text-[13px] font-medium text-white shadow-raise transition-colors hover:bg-zinc-800"
        >
          {signedIn ? "Open library" : "Sign in"}
        </Link>
      </header>

      {/* Offsets the fixed header. */}
      <main className="flex-1 pt-14">
        <section className="mx-auto w-full max-w-5xl px-4 pb-12 pt-12 sm:px-6 sm:pb-14 sm:pt-20">
          <div className="max-w-2xl animate-rise-in">
            <p className="text-2xs font-semibold uppercase tracking-[0.14em] text-zinc-500">Internal image library</p>
            <h1 className="mt-3 text-[1.75rem] font-semibold leading-[1.15] tracking-tight text-zinc-900 sm:text-[2.25rem] lg:text-[2.75rem] lg:leading-[1.1]">
              Store images once, link to them anywhere.
            </h1>
            <p className="mt-4 text-[15px] leading-relaxed text-zinc-600">
              An administrator uploads an image, gives it a name, and gets a permanent URL served from Cloudflare&apos;s
              edge network. Files are organised in folders, and the folder path is the URL path.
            </p>
            <div className="mt-7 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
              <Link
                to={target}
                className="inline-flex h-11 w-full items-center justify-center rounded-xl bg-zinc-900 px-5 text-sm font-medium text-white shadow-raise transition-colors hover:bg-zinc-800 sm:w-auto"
              >
                {signedIn ? "Open library" : "Sign in to continue"}
              </Link>
              <span className="text-[13px] text-zinc-500">Access is limited to the administrator account.</span>
            </div>
          </div>

          <div className="glass mt-10 rounded-2xl p-4 shadow-glass sm:mt-12 sm:p-6">
            <p className="text-2xs font-medium uppercase tracking-wide text-zinc-500">What a finished link looks like</p>
            {/* Wraps between path segments instead of forcing a sideways scroll. */}
            <code className="mt-3 block break-all rounded-xl bg-zinc-900/95 px-3 py-3 font-mono text-xs leading-relaxed text-zinc-100 sm:px-4 sm:text-[13px]">
              https://img-cdn.bt4team.com/products/phones/iphone-17-pro.webp
            </code>
            <p className="mt-3 text-[13px] leading-relaxed text-zinc-600">
              That address returns the image itself, never an HTML page, and carries a one year immutable cache header.
              Renaming or moving the image changes the address, and the interface warns you before it does.
            </p>
          </div>
        </section>

        <section className="mx-auto w-full max-w-5xl px-4 pb-12 sm:px-6 sm:pb-14">
          <h2 className="text-base font-semibold tracking-tight text-zinc-900 sm:text-lg">What it does</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {CAPABILITIES.map((c) => (
              <div key={c.title} className="glass rounded-xl p-4 transition-colors hover:bg-white/75">
                <h3 className="text-[13px] font-semibold text-zinc-900">{c.title}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-zinc-600">{c.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-5xl px-4 pb-16 sm:px-6 sm:pb-20">
          <h2 className="text-base font-semibold tracking-tight text-zinc-900 sm:text-lg">Delivery and limits</h2>
          <dl className="glass mt-5 divide-y divide-white/70 rounded-2xl px-4 shadow-glass sm:px-5">
            {DELIVERY.map((d) => (
              <div
                key={d.term}
                className="flex flex-col gap-1 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-6"
              >
                <dt className="flex-shrink-0 text-[13px] text-zinc-600">{d.term}</dt>
                <dd className="break-words font-mono text-xs text-zinc-900 sm:text-right sm:text-[13px]">
                  {d.detail}
                </dd>
              </div>
            ))}
          </dl>
          <p className="mt-4 text-[13px] leading-relaxed text-zinc-500">
            Uploads are checked against the file signature rather than the name or the declared type, so a renamed
            document is rejected before it reaches storage. SVG uploads are disabled by default because SVG files can
            carry active content.
          </p>
        </section>
      </main>

      <div className="glass glass-quiet rounded-none border-x-0 border-b-0">
        <Footer inset />
      </div>
    </div>
  );
}
